import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import SortableRow, {
  StaticRow, addonShape, statusOf, DRAG_DISABLED_HINT,
} from './SortableItem';

// The first three tabs are the LIFECYCLE (the `status` column); the middle three
// are WHERE a product sells (the audience flags). Deliberately not merged: an
// add-on can be a draft that is already marked for seniors, and the audience
// tabs answer "what does this storefront carry", drafts included.
// ⚠️ `storefront` IS WHAT MAKES A TAB DRAGGABLE. Each storefront keeps its own
// order, so dragging on Seniors moves what seniors see and nothing else. The
// lifecycle tabs (Active, Draft, Hidden) span all three storefronts -- there is
// no single order to write from them, so they do not drag.
const TABS = [
  { key: 'active', label: 'Active', match: (item) => statusOf(item) === 'active' },
  { key: 'draft', label: 'Draft', match: (item) => statusOf(item) === 'draft' },
  {
    key: 'seniors', label: 'Seniors', storefront: 'senior', match: (item) => item.is_senior,
  },
  {
    key: 'elementary', label: 'Elementary', storefront: 'elementary', match: (item) => item.is_elementary,
  },
  {
    key: 'gifts', label: 'Gifts', storefront: 'gifts', match: (item) => item.is_holiday,
  },
  { key: 'hidden', label: 'Hidden', match: (item) => statusOf(item) === 'hidden' },
];

const SORTS = [
  { key: 'position', label: 'Store order' },
  { key: 'name', label: 'Name A–Z' },
  { key: 'price_asc', label: 'Price low to high' },
  { key: 'price_desc', label: 'Price high to low' },
];

// Up/down arrows: the button opens a menu, so without a mark it reads as just
// another label. aria-hidden keeps the accessible name as "Sort: <option>".
const SortIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M7 4v16" />
    <path d="M3 8l4-4 4 4" />
    <path d="M17 20V4" />
    <path d="M13 16l4 4 4-4" />
  </svg>
);

const AUDIENCES = [
  { key: 'is_senior', label: 'Seniors' },
  { key: 'is_elementary', label: 'Elementary' },
  { key: 'is_holiday', label: 'Gifts' },
];

// The app runs `protect_from_forgery with: :null_session`, so a token-less write
// is not rejected outright -- measured 2026-09-30, the reorder call has been
// saving without one for as long as it has existed. It is sent anyway, because
// that is what every other axios caller in this repo does (utils/freeMagnets.js,
// utils/buyAllDigital.js) and because the protection is only one config line
// away from being the strict kind.
const csrfHeaders = () => {
  const tag = document.querySelector('meta[name="csrf-token"]');

  return tag ? { 'X-CSRF-Token': tag.content } : {};
};

const sortPrice = (item) => (item.price_with != null ? item.price_with : item.price_without);

// null/blank always sorts last, whichever direction is asked for.
const byKey = (key) => (a, b) => {
  if (key === 'name') return String(a.name || '').localeCompare(String(b.name || ''));

  const av = sortPrice(a);
  const bv = sortPrice(b);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;

  return key === 'price_desc' ? bv - av : av - bv;
};

// ⚠️ MIRRORS THE SQL THE STOREFRONTS ARE SERVED WITH --
// `COALESCE(<storefront>_position, position), position` in
// `Addon.in_storefront_order`. The admin list has to show a tab in the order its
// customers get, or a drag is saved, served, and then invisible on reload.
const POSITION_FIELD = {
  senior: 'senior_position',
  elementary: 'elementary_position',
  gifts: 'gifts_position',
};

const rankIn = (item, storefront) => {
  const own = item[POSITION_FIELD[storefront]];

  return own == null ? item.position : own;
};

const byStorefront = (storefront) => (a, b) => (
  (rankIn(a, storefront) || 0) - (rankIn(b, storefront) || 0)
  || (a.position || 0) - (b.position || 0)
);

const NO_CATEGORY = 'none';
const ALL_CATEGORIES = 'all';

const SortableAddons = ({ items: initialItems, categories, initialCategory }) => {
  // `items` is every add-on, in the shared `position` order. Each row also
  // carries the three storefront orders, because a storefront tab is shown in
  // ITS order, not this one. Search and the non-store sorts are display-only and
  // must never write back, which is why dragging is refused unless the view
  // being shown IS a storefront's stored order.
  const [items, setItems] = useState(initialItems);
  const [tab, setTab] = useState('active');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('position');
  const [category, setCategory] = useState(initialCategory || ALL_CATEGORIES);
  const [selected, setSelected] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFlags, setBulkFlags] = useState({});
  const [message, setMessage] = useState(null);

  const saveTimer = useRef(null);
  // Read by the debounced save, which runs after the render that queued it.
  const storefrontRef = useRef(null);
  const selectAllRef = useRef(null);
  const rootRef = useRef(null);

  const trimmedQuery = query.trim();
  const { storefront } = TABS.find((option) => option.key === tab) || {};

  // A storefront tab, in store order. A category filter is allowed: the rows you
  // move are written back into the slots they already held in that storefront's
  // order, so narrowing the view cannot scramble what is hidden by it. A SEARCH
  // is not, because a search can leave two adjacent rows with a gap of unseen
  // products between them.
  const dragEnabled = sort === 'position' && Boolean(storefront) && trimmedQuery === '';

  const counts = useMemo(
    () => TABS.reduce((acc, { key, match }) => ({ ...acc, [key]: items.filter(match).length }), {}),
    [items],
  );

  const visible = useMemo(() => {
    const matcher = TABS.find((t) => t.key === tab).match;
    const needle = trimmedQuery.toLowerCase();

    const filtered = items.filter((item) => {
      if (!matcher(item)) return false;
      if (category === NO_CATEGORY && item.category_id) return false;
      if (category !== ALL_CATEGORIES && category !== NO_CATEGORY
        && String(item.category_id) !== String(category)) return false;
      if (!needle) return true;

      return `${item.name || ''} ${item.category_label || ''}`.toLowerCase().includes(needle);
    });

    if (sort !== 'position') return [...filtered].sort(byKey(sort));

    // A storefront tab reads its own column; the lifecycle tabs span all three
    // and have only the shared order to show.
    return storefront ? [...filtered].sort(byStorefront(storefront)) : filtered;
  }, [items, tab, trimmedQuery, sort, category, storefront]);

  const visibleIds = useMemo(() => visible.map((item) => item.id), [visible]);
  const selectedVisible = useMemo(
    () => selected.filter((id) => visibleIds.includes(id)),
    [selected, visibleIds],
  );

  // Close the sort / row / bulk popovers on an outside click, the way every
  // other menu on the page behaves.
  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (rootRef.current && rootRef.current.contains(event.target)) return;

      setOpenMenu(null);
      setSortOpen(false);
      setBulkOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);

    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!selectAllRef.current) return;

    selectAllRef.current.indeterminate = selectedVisible.length > 0 && selectedVisible.length < visibleIds.length;
  }, [selectedVisible, visibleIds]);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  // Saved on drop, not on a timer. The page used to PATCH all 51 rows every two
  // seconds for as long as it was open, whether or not anything had moved.
  const persistOrder = useCallback((ordered) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      axios.patch(
        '/admin/addons/reorder',
        { items: ordered.map(({ id }) => ({ id })), storefront: storefrontRef.current },
        { headers: csrfHeaders() },
      ).catch(() => setMessage('The new order could not be saved. Reload the page and try again.'));
    }, 400);
  }, []);

  // react-sortable hands back the VISIBLE list, which is not the whole
  // storefront: a category filter can be narrowing it. Writing that back as the
  // order would renumber the shown rows 1..n and leave the filtered-out ones on
  // numbers that now belong to somebody else.
  //
  // So the moved rows are written into the slots they already held in THIS
  // storefront's order, and that whole storefront list is what gets saved and
  // renumbered. (The copy matters too: react-sortable swaps IN PLACE and returns
  // that same reference, and useState bails on an identical one, so the row
  // would never visibly move.)
  storefrontRef.current = storefront;

  // Every row this tab sells, in this storefront's order -- the list the drag
  // rearranges and the endpoint renumbers 1..n.
  const storefrontItems = useMemo(() => {
    if (!storefront) return null;
    const matcher = TABS.find((t) => t.key === tab).match;

    return items.filter(matcher).sort(byStorefront(storefront));
  }, [items, tab, storefront]);

  const onSortItems = useCallback((orderedVisible) => {
    if (!storefrontItems) return;

    const shown = new Set(visibleIds);
    const next = [...storefrontItems];
    const slots = [];

    storefrontItems.forEach((item, index) => { if (shown.has(item.id)) slots.push(index); });
    slots.forEach((slot, i) => { next[slot] = orderedVisible[i]; });

    // ⚠️ RENUMBER LOCALLY TOO, or the next drag rebuilds from the order the page
    // loaded with and silently undoes this one. These are the same numbers the
    // endpoint is about to write.
    const field = POSITION_FIELD[storefront];
    const saved = new Map(next.map((item, index) => [item.id, index + 1]));

    setItems(items.map((item) => (
      saved.has(item.id) ? { ...item, [field]: saved.get(item.id) } : item
    )));
    persistOrder(next);
  }, [items, storefrontItems, visibleIds, storefront, persistOrder]);

  const toggleSelect = useCallback((id) => {
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  }, []);

  // Select-all covers the rows on screen, never the ones a tab or search is
  // hiding -- a bulk delete has to mean what the page is showing.
  const toggleSelectAll = () => {
    setSelected((current) => (
      selectedVisible.length === visibleIds.length
        ? current.filter((id) => !visibleIds.includes(id))
        : [...new Set([...current, ...visibleIds])]
    ));
  };

  const toggleMenu = useCallback((id) => {
    setOpenMenu((current) => (current === id ? null : id));
    setSortOpen(false);
  }, []);

  const removeLocally = (ids) => {
    setItems((current) => current.filter((item) => !ids.includes(item.id)));
    setSelected((current) => current.filter((id) => !ids.includes(id)));
  };

  const deleteOne = useCallback((item) => {
    /* eslint-disable-next-line no-alert */
    if (!window.confirm(`Delete “${item.name}”? This cannot be undone.`)) return;

    axios.delete(item.show_path, { headers: csrfHeaders() })
      .then(() => { removeLocally([item.id]); setOpenMenu(null); setMessage(null); })
      .catch(() => setMessage(`“${item.name}” could not be deleted.`));
  }, []);

  const deleteSelected = () => {
    const ids = [...selectedVisible];
    /* eslint-disable-next-line no-alert */
    if (!window.confirm(`Delete ${ids.length} add-on${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;

    axios.delete('/admin/addons/bulk_destroy', { data: { ids }, headers: csrfHeaders() })
      .then(({ data }) => { removeLocally(data.ids || ids); setMessage(null); })
      .catch(() => setMessage('Those add-ons could not be deleted.'));
  };

  // A flag the admin never touched is not sent, so a mixed selection keeps the
  // audiences that were not part of the decision.
  const flagState = (key) => {
    if (key in bulkFlags) return bulkFlags[key];
    if (selectedVisible.length === 0) return false;

    return selectedVisible.every((id) => items.find((item) => item.id === id)[key]);
  };

  const flagMixed = (key) => !(key in bulkFlags)
    && selectedVisible.some((id) => items.find((item) => item.id === id)[key])
    && !selectedVisible.every((id) => items.find((item) => item.id === id)[key]);

  // Sets the LIFECYCLE and nothing else. Taking a product off sale used to mean
  // clearing the flags that say where it sold, which is exactly the information
  // you need to put it back.
  const hideSelected = () => {
    const ids = [...selectedVisible];

    axios.patch(
      '/admin/addons/bulk_update',
      { ids, status: 'hidden' },
      { headers: csrfHeaders() },
    ).then(({ data }) => {
      const updated = data.rows || [];
      setItems((current) => current.map((item) => {
        const row = updated.find((next) => next.id === item.id);

        return row ? { ...item, ...row } : item;
      }));
      setMessage(null);
    }).catch(() => setMessage('Those add-ons could not be hidden.'));
  };

  const applyFlags = () => {
    const ids = [...selectedVisible];

    axios.patch(
      '/admin/addons/bulk_update',
      { ids, flags: bulkFlags },
      { headers: csrfHeaders() },
    ).then(({ data }) => {
      const updated = data.rows || [];
      // Merged, not replaced: a bulk action's rows carry no `sales` (the 30-day
      // report is not run for them), and replacing would blank the column.
      setItems((current) => current.map((item) => {
        const row = updated.find((next) => next.id === item.id);

        return row ? { ...item, ...row } : item;
      }));
      setBulkOpen(false);
      setBulkFlags({});
      setMessage(data.failed && data.failed.length
        ? `${data.failed.length} add-on${data.failed.length === 1 ? ' was' : 's were'} left unchanged: ${
          data.failed.map((row) => `${row.name} (${row.errors.join('; ')})`).join(', ')}`
        : null);
    }).catch(() => setMessage('Those add-ons could not be updated.'));
  };

  const clearSearch = () => setQuery('');

  const sortLabel = SORTS.find((option) => option.key === sort).label;

  const rows = visible.map((item, index) => {
    const shared = {
      item,
      selected: selected.includes(item.id),
      onToggleSelect: toggleSelect,
      menuOpen: openMenu === item.id,
      onToggleMenu: toggleMenu,
      onDelete: deleteOne,
      dragEnabled,
    };

    if (!dragEnabled) return <StaticRow key={item.id} {...shared} />;

    return (
      <SortableRow
        key={item.id}
        sortId={index}
        items={visible}
        onSortItems={onSortItems}
        {...shared}
      />
    );
  });

  return (
    <div className="aa-card" ref={rootRef}>
      {/* Sort sits on the TAB line, not in the toolbar below: both answer "which
          add-ons am I looking at, in what order", and they gate dragging
          together. The toolbar underneath is for narrowing the list. */}
      <div className="aa-tabs-row">
        <div className="aa-tabs" role="tablist" aria-label="Filter add-ons">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              className="aa-tab"
              aria-selected={tab === key}
              onClick={() => { setTab(key); setOpenMenu(null); }}
            >
              {label}
              <span className="aa-count">{counts[key]}</span>
            </button>
          ))}
        </div>

        <div className="aa-menu-wrap">
          <button
            type="button"
            className="aa-btn"
            aria-haspopup="menu"
            aria-expanded={sortOpen}
            onClick={() => { setSortOpen((open) => !open); setOpenMenu(null); }}
          >
            <SortIcon />
            {`Sort: ${sortLabel}`}
          </button>

          {sortOpen ? (
            <div className="aa-menu" role="menu">
              {SORTS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={sort === key}
                  onClick={() => { setSort(key); setSortOpen(false); }}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="aa-toolbar">
        <div className="aa-search">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input
            type="search"
            value={query}
            placeholder="Search by name or category"
            aria-label="Search add-ons by name or category"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <div className="aa-filter">
          <label className="aa-sr-only" htmlFor="aa-category-filter">Category</label>
          <select
            id="aa-category-filter"
            className="aa-select"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value={ALL_CATEGORIES}>All categories</option>
            {categories.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
            <option value={NO_CATEGORY}>No category</option>
          </select>
        </div>

        <a className="aa-toolbar-link" href="/admin/addon_categories">Manage categories</a>

      </div>

      {message ? <p className="aa-note">{message}</p> : null}

      {selectedVisible.length > 0 ? (
        <div className="aa-bulk">
          <span className="aa-bulk-count">{`${selectedVisible.length} selected`}</span>

          <div className="aa-menu-wrap">
            <button
              type="button"
              className="aa-btn"
              aria-haspopup="menu"
              aria-expanded={bulkOpen}
              onClick={() => setBulkOpen((open) => !open)}
            >
              Show to…
            </button>

            {bulkOpen ? (
              <div className="aa-menu" role="menu">
                <div className="aa-bulk-form">
                  {/* Each input is NESTED in its label rather than wired to it
                      by htmlFor. With both, a click on the checkbox is
                      forwarded a second time by label activation and the box
                      toggles straight back to where it started. */}
                  {AUDIENCES.map(({ key, label }) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        className="aa-checkbox"
                        checked={flagState(key)}
                        ref={(node) => { if (node) node.indeterminate = flagMixed(key); }}
                        onChange={(event) => {
                          // Read the value NOW. The state updater runs after
                          // React has reset this controlled input to the value
                          // it currently holds, so `event.target.checked` read
                          // inside the updater reports the OLD value and the
                          // box appears not to tick at all.
                          const { checked } = event.target;

                          setBulkFlags((current) => ({ ...current, [key]: checked }));
                        }}
                      />
                      {label}
                    </label>
                  ))}

                  <button
                    type="button"
                    className="aa-btn aa-btn--primary"
                    disabled={Object.keys(bulkFlags).length === 0}
                    onClick={applyFlags}
                  >
                    Apply
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <button type="button" className="aa-btn" onClick={hideSelected}>Hide from store</button>
          <button type="button" className="aa-btn aa-btn--danger" onClick={deleteSelected}>Delete</button>
        </div>
      ) : (
        <div className="aa-grid aa-thead">
          <div>
            <input
              type="checkbox"
              className="aa-checkbox"
              ref={selectAllRef}
              checked={visibleIds.length > 0 && selectedVisible.length === visibleIds.length}
              onChange={toggleSelectAll}
              aria-label="Select all add-ons shown"
            />
          </div>
          <div />
          <div>Add-on</div>
          <div>Status</div>
          <div>Shown to</div>
          <div>With package</div>
          <div>On its own</div>
          <div />
        </div>
      )}

      {rows.length > 0 ? rows : (
        <div className="aa-empty">
          <h2>{trimmedQuery ? `No add-ons match “${trimmedQuery}”` : 'No add-ons in this tab'}</h2>
          <p>
            {(() => {
              if (trimmedQuery) return 'Check the spelling, or clear the search to see the whole list.';

              if (category !== ALL_CATEGORIES) return 'Nothing is filed under this category yet.';
              if (tab === 'hidden') return 'Nothing has been withdrawn from the store.';
              if (tab === 'draft') return 'Nothing is part-built — new add-ons start here.';

              return tab === 'active'
                ? 'Nothing is on sale right now — check the Draft and Hidden tabs.'
                : 'Nothing is filed under this audience yet.';
            })()}
          </p>
          {trimmedQuery ? <button type="button" className="aa-btn" onClick={clearSearch}>Clear search</button> : null}
        </div>
      )}

      <div className="aa-foot">
        <span>
          {dragEnabled
            ? `Drag rows to change the order ${TABS.find((option) => option.key === tab).label} customers see them in.`
            : DRAG_DISABLED_HINT}
        </span>
        <span>{`Showing ${visible.length} of ${items.length}`}</span>
      </div>
    </div>
  );
};

SortableAddons.propTypes = {
  items: PropTypes.arrayOf(addonShape).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
  initialCategory: PropTypes.string,
};

SortableAddons.defaultProps = {
  categories: [],
  initialCategory: null,
};

export default SortableAddons;
